import { FC, useState } from "react";

import { ChatHelper } from "../../helpers/chatHelper";
import { ChatLetterOutput, IFileAttachment } from "../../../types/chat";
import {
  downloadAttachmentsAsZip,
  fetchAppealLetter,
  forceDownloadFile,
} from "../../../services/fileDownloadService";
import { CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import { VerticalEllipsisIcon } from "../../icons";
import { useCapabilities } from "../../../contexts/permissions/useCapabilities";

interface FilesAttachedMenuProps {
  files: IFileAttachment[];
  /** Presence of a generated letter; the content comes from the backend. */
  letter?: ChatLetterOutput | null;
  claimId?: string;
  chatId?: string | null;
}

const resolveFileId = (file: IFileAttachment): string | null => {
  let fileId = (file.id as string | null) ?? null;
  if (!fileId) {
    const fileUrl = file.file_url || file.url;
    fileId = ChatHelper.extractFileIdFromUrl(fileUrl as string);
  }
  return fileId;
};

const FilesAttachedMenu: FC<FilesAttachedMenuProps> = ({ files, letter, claimId, chatId }) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);
  const { canDownloadAttachments } = useCapabilities();

  const totalItems = files.length + (letter?.content ? 1 : 0);
  const menuOpen = Boolean(menuAnchor);

  // Downloading is this menu's only action, and the backend refuses
  // /files/<id>/download/ with 403 capability_denied when the flag is withheld
  // (REV-1974). Rendering the trigger anyway would leave a button that fails on
  // click, so the whole menu goes rather than an empty one.
  if (!canDownloadAttachments) return null;

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (downloading) return;
    setMenuAnchor(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleDownloadAllFiles = async () => {
    if (totalItems === 0 || downloading) return;

    // Single attachment and no letter → direct download (no zip needed)
    if (files.length === 1 && !letter?.content) {
      handleCloseMenu();
      const file = files[0];
      const name =
        file.display_name || file.original_name || file.file_name || file.name || "download";
      const id = resolveFileId(file);

      if (id) {
        await forceDownloadFile(id, name);
      } else {
        ChatHelper.downloadFile(file);
      }
      return;
    }

    handleCloseMenu();
    setDownloading(true);

    try {
      let extraEntries: Array<{ name: string; blob: Blob }> | undefined;

      // The letter in the zip is the same render the individual download buttons
      // serve, so the two cannot drift apart (REV-2047). A letter that fails to
      // render is left out rather than failing the whole zip, as before.
      if (letter?.content && chatId) {
        for (const format of ["pdf", "docx"] as const) {
          const fetchedLetter = await fetchAppealLetter(chatId, format, claimId);
          if (fetchedLetter.success && fetchedLetter.blob) {
            extraEntries = [
              ...(extraEntries ?? []),
              { name: fetchedLetter.filename!, blob: fetchedLetter.blob },
            ];
          }
        }
      }

      const result = await downloadAttachmentsAsZip(files, "attachments.zip", extraEntries);
      if (!result.success) {
        console.error(result.error ?? "Zip download failed");
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={
          downloading ? "Downloading attachments" : "File list options"
        }
        aria-busy={downloading}
        aria-controls={menuOpen ? "file-viewer-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        onClick={handleOpenMenu}
        sx={{
          color: "text.secondary",
          flexShrink: 0,
          pointerEvents: downloading ? "none" : undefined,
        }}
      >
        {downloading ? (
          <CircularProgress
            size={16}
            thickness={5}
            sx={{ color: "text.secondary" }}
          />
        ) : (
          <VerticalEllipsisIcon className="w-4 h-4" />
        )}
      </IconButton>
      
      <Menu
        id="file-viewer-actions-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem onClick={handleDownloadAllFiles} disabled={downloading}>
          {totalItems > 1 ? "Download All" : "Download File"}
        </MenuItem>
      </Menu>
    </>
  );
};

export default FilesAttachedMenu;
